import AsyncView from '@pixelunion/shopify-asyncview';
import rimg from '@pixelunion/rimg-shopify';
import Modal from './Modal';
import Product from '../sections/Product';

/**
 * Updates element IDs to ensure they are unique within the document by checking for conflicts.
 * Also checks associated labels and updates the `for` attribute if required.
 * @param {NodeList} idEls Elements to check for ID uniqueness within the document
 * @param {NodeList} labels Labels to update if element with associated ID is updated.
 */
function makeIDsUnique(idEls, labels) {
  idEls.forEach(idEl => {
    const oldId = idEl.getAttribute('id');
    const duplicateCount = document.querySelectorAll(`[id$="${oldId}"]`).length;

    if (duplicateCount > 0) {
      const newId = `${duplicateCount}${oldId}`;
      idEl.setAttribute('id', newId);

      // update label
      labels.forEach(label => {
        if (label.matches(`[for="${oldId}"]`)) {
          label.setAttribute('for', newId);
        }
      });
    }
  });
}

export default class ProductQuickshop {
  constructor(options) {
    this.options = options;
    this.el = null;
    this.modal = null;
    this.product = null;

    this.isLoaded = false;
    this.loader = null;

    this.isOpen = false;
  }

  open() {
    const loader = !this.isLoaded && this.loader ? this.loader : this.load();

    const { id, url, trigger } = this.options;

    const modalCallbacks = {
      onClose: () => this.close(),
    };

    this.modal = new Modal(modalCallbacks);
    this.modal.open(
      `#${id} [data-product-quickshop-url="${url}"]`,
      'quickshop',
      trigger,
    );

    this.isOpen = true;

    loader.then(() => {
      if (!this.isOpen) return;
      this.modal.position();
      rimg.watch(this.el);

      if (window.Shopify && Shopify.PaymentButton) {
        Shopify.PaymentButton.init();
      }
    });
  }

  close() {
    if (this.isOpen) {
      const { trigger } = this.options;
      trigger.focus();

      this.modal.unload();

      this.isOpen = false;
    }
  }

  unload() {
    if (this.isLoaded || this.loader) {
      const loader = this.loader || Promise.resolve();

      this.close();

      return loader
        .then(() => {
          if (this.product) {
            this.product.unload();
          }
        });
    }

    return Promise.resolve();
  }

  load() {
    // If we're already loaded, return true
    if (this.isLoaded) {
      return Promise.resolve(true);
    }

    // If we're currently loading, return the existing promise
    if (this.loader) {
      return this.loader;
    }

    const { el, url } = this.options;

    this.loader = AsyncView.load(url, { view: 'quickshop' })
      .then(response => {
        const { data, html } = response;
        const container = document.createElement('div');

        container.innerHTML = html;

        makeIDsUnique(container.querySelectorAll('select[id], input[id]'), container.querySelectorAll('label[for]'));

        const targetContainer = this.isOpen ? this.modal.modalEl.querySelector('[data-modal-content]') : el;

        const spinner = targetContainer.querySelector('.quickshop__loading-state');
        spinner.parentNode.removeChild(spinner);

        this.el = targetContainer.appendChild(container);

        this.product = new Product({ el: container, data });
        this.isLoaded = true;
      })
      .then(() => {
        this.loader = null;
        return true;
      })
      .catch(e => {
        console.warn('Unable to load quickshop', e);
        this.loader = null;
        return false;
      });

    return this.loader;
  }
}
